import {
  Component,
  computed,
  inject,
  input,
  model,
  signal,
  ChangeDetectionStrategy
} from "@angular/core";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import {
  FormTemplate,
  PermissionType,
  RawPermissions,
  ViewPermission,
} from "../forms.model";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Observable } from "rxjs";

@Component({
  selector: "app-form-assign-perm-dialog",
  templateUrl: "./form-assign-perm-dialog.component.html",
  styleUrl: "./form-assign-perm-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FormAssignPermDialogComponent {
  readonly dialogRef = inject(MatDialogRef<FormAssignPermDialogComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA); //TODO
  readonly form = model(this.data?.form);

  protected readonly nameResolver = this.data.nameResolver;
  protected readonly customerLoader: (uuids: string[]) => Observable<unknown> =
    this.data.customerLoader;

  showOnlyActive = signal<boolean>(false);
  formTemplate = signal<FormTemplate | null>(null);
  isLoading = signal<boolean>(false);
  rawPermissions = signal<RawPermissions | null>(null);
  // bumped when missing customers are loaded, so their names get resolved again
  private customersLoaded = signal(0);

  parsedEdit = computed(() => {
    this.customersLoaded();
    return this.parseAction(this.rawPermissions()?.edit);
  });
  parsedSave = computed(() => {
    this.customersLoaded();
    return this.parseAction(this.rawPermissions()?.save);
  });

  private parseAction(actionData: any) {
    if (!actionData) return { included: [], excluded: [] };

    return {
      included: (actionData.included || []).flat().map(this.mapToView),
      excluded: (actionData.excluded || []).map(this.mapToView),
    };
  }

  private mapToView = (rawString: string): ViewPermission => {
    const [type, uuid] = rawString.split("$");
    const resolver = this.nameResolver;

    const displayName =
      resolver && uuid
        ? resolver(type, uuid)
        : uuid
          ? uuid.substring(0, 8) + "..."
          : "No data";

    return {
      type: type ? type.toUpperCase() : "UNKNOWN",
      uuid: uuid,
      name: displayName,
      raw: rawString,
    };
  };

  onShowOnlyActiveChange($event: MatSlideToggleChange) {
    this.showOnlyActive.set($event.checked);
  }

  onFormSelected(formTemplate: FormTemplate) {
    this.formTemplate.set(formTemplate);
    this.rawPermissions.set(formTemplate?.permissions as any);
    this.loadCustomerNames(this.rawPermissions());
  }

  private loadCustomerNames(permissions: RawPermissions | null) {
    if (!permissions || !this.customerLoader) return;
    const customerUuids = [permissions.edit, permissions.save]
      .flatMap((action) => [
        ...(action?.included || []).flat(),
        ...(action?.excluded || []),
      ])
      .map((raw) => raw.split("$"))
      .filter(([type]) => type === PermissionType.CUSTOMER)
      .map(([, uuid]) => uuid);
    if (customerUuids.length === 0) return;
    this.customerLoader(customerUuids).subscribe(() =>
      this.customersLoaded.update((v) => v + 1),
    );
  }

  formIsLoading(isLoading: boolean) {
    this.isLoading.set(isLoading);
  }

  onCloseClick(): void {
    this.dialogRef.close(false);
  }

  onSave() {
    if (this.formTemplate().permissions) {
      this.dialogRef.close(this.formTemplate().permissions);
    }
  }
}
